import React from 'react';
import { Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';

interface CampaignStatistics {
  totalUsers: number;
  verifiedUsers: number;
  messages: number | null;
  transactions: {
    total: number;
    count: number;
    breakdown: Record<string, number>;
  };
  cost: number;
  arpu: number;
  arpuUsd: number;
  romi: number;
}

interface OrderStatsTooltipProps {
  statistics: CampaignStatistics;
}

const formatPercent = (value: number, total: number): string => {
  if (!total) return '0%';
  const percent = (value / total) * 100;
  return `${percent.toFixed(2)}%`;
};

const OrderStatsTooltip: React.FC<OrderStatsTooltipProps> = ({ statistics }) => {
  const { t } = useTranslations();

  const totalFans = statistics.totalUsers || 0;
  const verifiedUsers = statistics.verifiedUsers || 0;
  const messagesCount = statistics.messages ?? 0;
  const breakdown = statistics.transactions?.breakdown || {};

  const purchasesCount = statistics.transactions?.count ?? 0;
  const purchases500 = breakdown['500'] || 0;

  const stats = [
    {
      label: t('orders.stats.subscriptions'),
      value: verifiedUsers,
      percentage: formatPercent(verifiedUsers, totalFans),
    },
    {
      label: t('orders.stats.purchases'),
      value: purchasesCount,
      percentage: formatPercent(purchasesCount, verifiedUsers),
    },
    {
      label: t('orders.stats.purchases500'),
      value: purchases500,
      percentage: formatPercent(purchases500, purchasesCount),
    },
    {
      label: t('orders.stats.messages'),
      value: messagesCount,
      percentage: formatPercent(messagesCount, verifiedUsers),
    },
  ];

  return (
    <div className="">
      <div className="mb-2">
        <Typography variant="body2" className="text-auth-ink ">
          {t('orders.stats.title')}
        </Typography>
      </div>

      <div className="flex items-center mb-2">
        <div className="w-[10rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('orders.stats.parameter')}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('orders.stats.fans')}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('orders.stats.percentage')}
          </Typography>
        </div>
      </div>

      <div>
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center py-1.5">
            <div className="w-[10rem]">
              <Typography variant="body3" className="text-auth-muted">
                {stat.label}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.value}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.percentage}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderStatsTooltip;
